import { Component, type ErrorInfo, type ReactNode } from 'react';

/**
 * 一屏出错,就只让那一屏出错。
 *
 * ★ 为什么值得有它:React 在渲染里抛异常时会**把整棵树卸载掉**。
 *   没有边界的话,一个字段读成 undefined 的后果是
 *   **整个司机端变成一片空白** —— 连返回按钮都没有,
 *   司机只能杀掉 app 重开,而他在路上。
 *
 *   这一个 session 里就撞了三次白屏:Hook 写在早退后面、
 *   const 在声明前被用、以及 `task?.pickup.name`(只兜住了第一段)。
 *   前两个是我的错、第三个是历史遗留,而它们的**症状完全一样**:
 *   屏幕全白、控制台里什么都看不到(有的只在 Vite 的 server 日志里)。
 *
 * ★ 兜住之后给的是「这一屏打不开 + 返回」,不是一句「出错了」:
 *   司机此刻要的是回到任务列表接着干活。
 *
 * ★ 同时把错误打到 console。吞掉的话,这类问题就只能靠用户描述来猜,
 *   而「白屏」这个描述指向不了任何具体代码。
 */
export class ErrorBoundary extends Component<
  { children: ReactNode; onBack?: () => void; label?: string },
  { err: Error | null }
> {
  override state: { err: Error | null } = { err: null };

  static getDerivedStateFromError(err: Error): { err: Error } {
    return { err };
  }

  override componentDidCatch(err: Error, info: ErrorInfo): void {
    console.error('这一屏渲染出错', err, info.componentStack);
  }

  override render(): ReactNode {
    if (!this.state.err) return this.props.children;
    return (
      <div className="fixed inset-0 z-[80] grid place-items-center bg-white p-6 dark:bg-slate-900">
        <div className="max-w-sm text-center">
          <p className="text-lg font-semibold text-slate-900 dark:text-slate-100">
            {this.props.label ?? '这一屏'}打不开
          </p>
          <p className="mt-2 text-sm text-slate-500">
            这一单的数据有点问题,已经记到日志里了。其它单不受影响。
          </p>
          {this.props.onBack && (
            <button
              onClick={() => {
                this.setState({ err: null });
                this.props.onBack?.();
              }}
              className="mt-4 h-11 w-full rounded-xl bg-brand-500 font-medium text-white"
            >
              返回任务列表
            </button>
          )}
        </div>
      </div>
    );
  }
}
